import {
	KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS,
	PERMISSIONS_SETTING_IDS,
	WINDOWS_PERMISSIONS_SETTING_IDS,
	MOUSE_SETTING_IDS,
	COLLECT_LOGS_SETTING_IDS,
} from './SettingIds';

import { RadioSettingOption } from '../components/SettingCards/RadioSetting/RadioSetting';
import {
	MatchDeviceDisplayIcon,
	ScaleHighResolutionIcon,
	OSScaleResolutionIcon,
} from '../Icons/DisplayResolutionIcons/DisplayResolutionIcons';
import {
	DISPLAY_SETTING_IDS,
	KEYBOARD_SETTING_IDS,
	PRIVACY_SECURITY_SETTING_IDS,
	TLS_SETTING_IDS,
	WORKSPACE_UPDATE_SETTING_IDS,
	GENERAL_SETTING_IDS,
	SHORTCUT_SETTING_IDS,
	CAMERA_SETTING_IDS,
	AUDIO_SETTING_IDS,
	DEVICE_SETTING_IDS,
} from './SettingIds';

// Enum for Setting Types
export const enum SettingType {
	Plain = 'plain',
	TOGGLE = 'toggle',
	RADIO = 'radio',
	ICON_RADIO = 'iconradio',
	BUTTON = 'button',
	NAVIGATION = 'navigation',
	DROPDOWN = 'dropdown',
	FILE_DOWNLOAD = 'file-download',
	TEXT_INPUT = 'text-input',
	UPDATE_WORKSPACE = 'updateworkspace',
	TOGGLE_SETTING_GROUP = 'toggle-setting-group',
	RADIO_SETTING_GROUP = 'radio-setting-group',
	PERMISSIONS_SETTING = 'permissions-setting',
}

// Base Settings Configuration Interface
export interface BaseSettingConfig {
	title: string;
	description: string;
	type: SettingType;
	showContentSeparator?: boolean;
	ariaLabel?: string;
	learnMoreUrl?: string;
	learnMoreText?: string;
	subSettings?: Array<SubSettingConfig>;
	deprecated?: boolean;
}

export interface PlainSettingConfig extends BaseSettingConfig {
	type: SettingType.Plain;
}

export interface SubSettingConfig extends BaseSettingConfig {
	settingId?: string;
}

export interface TextInputSettingConfig extends BaseSettingConfig {
	type: SettingType.TEXT_INPUT;
	placeholder: string;
	label: string;
	helpText?: string;
}

export interface IconRadioOptionConfig {
	value: string;
	label: string;
	description: string;
	icon: React.ComponentType<{ className?: string }>;
}

// Settings Configuration Interfaces
export interface ToggleSettingConfig extends BaseSettingConfig {
	type: SettingType.TOGGLE;
}

export interface RadioSettingConfig extends BaseSettingConfig {
	type: SettingType.RADIO;
	options: RadioSettingOption[];
	showHelpIcon?: boolean;
	helpText?: string;
}

export interface IconRadioSettingConfig extends BaseSettingConfig {
	type: SettingType.ICON_RADIO;
	options: IconRadioOptionConfig[];
}

export interface ButtonSettingConfig extends BaseSettingConfig {
	type: SettingType.BUTTON;
	buttons: Array<{
		label: string;
		variant: 'primary' | 'secondary';
	}>;
}

export interface NavigationSettingConfig extends BaseSettingConfig {
	type: SettingType.NAVIGATION;
	onNavigating: () => void;
}

export interface DropdownSettingConfig extends BaseSettingConfig {
	type: SettingType.DROPDOWN;
	label: string;
	options: Array<{ value: string; label: string; description?: string }>;
	helpText?: string;
}

// Custom setting configuration for file download location
export interface FileDownloadSettingConfig extends BaseSettingConfig {
	type: SettingType.FILE_DOWNLOAD;
	options: RadioSettingOption[];
	defaultPath?: string;
	allowCustomPath?: boolean;
	filePickerConfig?: {
		buttonLabel: string;
		placeholder: string;
	};
}

// Custom setting configuration for UpdateWorkspaceSetting component
export interface UpdateWorkspaceSettingConfig extends BaseSettingConfig {
	type: SettingType.UPDATE_WORKSPACE;
	states: {
		checking: {
			title: string;
			description: string;
		};
		uptodate: {
			title: string;
			description: string;
		};
		updateavailable: {
			title: string;
			description: string;
			downloadButtonLabel?: string;
		};
	};
}

// Custom setting configuration for Toggle Setting Group with sub-toggles
export interface ToggleSettingGroupConfig extends BaseSettingConfig {
	type: SettingType.TOGGLE_SETTING_GROUP;
	toggleOptions: Array<{
		id: string;
		title: string;
		description?: string;
	}>;
}

// Custom setting configuration for Radio Setting Group with sub-radios
export interface RadioSettingGroupConfig extends BaseSettingConfig {
	type: SettingType.RADIO_SETTING_GROUP;
	radioOptions: Array<{
		id: string;
		title: string;
		description?: string;
		options: RadioSettingOption[];
	}>;
}

export interface PermissionsSettingGroupConfig extends BaseSettingConfig {
	type: SettingType.PERMISSIONS_SETTING;
	subsettings: { [key: string]: PermissionsSettingConfig };
}

export interface PermissionsSettingConfig extends BaseSettingConfig {
	title: string;
	label: string;
	options: RadioSettingOption[];
}

export type SettingConfig =
	| PlainSettingConfig
	| ToggleSettingConfig
	| RadioSettingConfig
	| IconRadioSettingConfig
	| ButtonSettingConfig
	| NavigationSettingConfig
	| DropdownSettingConfig
	| FileDownloadSettingConfig
	| TextInputSettingConfig
	| UpdateWorkspaceSettingConfig
	| ToggleSettingGroupConfig
	| RadioSettingGroupConfig
	| PermissionsSettingGroupConfig;

/**
 * Flat key-value mapping of Setting ID → Setting Configuration
 * This is the single source of truth for all setting configurations
 */
export type SettingsDefinitionsMap = {
	[settingId: string]: SettingConfig;
};

/**
 * Centralized Settings Configurations
 * Flat key-value mapping: Setting ID → Setting Config
 */
export const SETTINGS_DEFINITIONS: SettingsDefinitionsMap = {
	// Display Settings
	[DISPLAY_SETTING_IDS.SESSION_RESOLUTION]: {
		type: SettingType.ICON_RADIO,
		title: 'Session resolution',
		description: 'Select your preferred resolution within a virtual session.',
		options: [
			{
				value: 'match-device',
				label: 'Match my device display settings',
				description: 'Matches your device resolution settings for better image quality.',
				icon: MatchDeviceDisplayIcon,
			},
			{
				value: 'scale-high-resolution',
				label: 'Scale for high resolution',
				description: 'Optimizes display for high-resolution screens.',
				icon: ScaleHighResolutionIcon,
			},
			{
				value: 'os-scale',
				label: 'Let the operating system scale the resolution',
				description: 'Uses operating system scaling for optimal display.',
				icon: OSScaleResolutionIcon,
			},
		],
	},
	[DISPLAY_SETTING_IDS.DESKTOP_DISPLAY]: {
		type: SettingType.RADIO,
		title: 'Desktop display',
		description: 'Select the preferred virtual desktop display on your computer.',
		deprecated: true,
		options: [
			{
				value: 'best-resolution',
				label: 'Best resolution',
				description: 'Display at the optimal resolution for your device.',
			},
			{
				value: 'scale-to-fit',
				label: 'Scale to fit',
				description: 'Scale the desktop to fit the window size.',
			},
			{
				value: 'actual-size',
				label: 'Actual size',
				description: 'Display the desktop at its actual size without scaling.',
			},
		],
	},
	[DISPLAY_SETTING_IDS.CAST_TO_WORKSPACE_HUB]: {
		type: SettingType.TOGGLE,
		title: 'Cast Citrix sessions to Workspace Hub-enabled devices',
		description: '',
	},
	[DISPLAY_SETTING_IDS.MONITOR_LAYOUT]: {
		type: SettingType.Plain,
		title: 'Monitor layout',
		description:
			'Click on the drawing board to split the screen. Right-click to select the primary monitor. Resize the window after settings are applied.',
	},

	// Keyboard Settings
	[KEYBOARD_SETTING_IDS.SYSTEM_SHORTCUTS]: {
		type: SettingType.RADIO,
		title: 'Keyboard system shortcuts take effect on',
		description:
			'To enable system shortcuts on HDX sessions, Citrix Viewer requires Accessibility permissions in your device system settings.',
		options: [
			{
				value: 'localDeviceOnly',
				label: 'Local device only',
				description:
					'Shortcuts only take effect on your local device, even while a desktop session is active.',
			},
			{
				value: 'fullscreenSessionOnly',
				label: 'Fullscreen session only',
				description:
					'Shortcuts only take effect when the desktop session is in full-screen mode.',
			},
			{
				value: 'anyActiveSession',
				label: 'Any active session',
				description: 'Shortcuts take effect on the active desktop session.',
			},
		],
		showHelpIcon: true,
		helpText:
			'To enable system shortcuts on HDX sessions, Citrix Viewer requires Accessibility permissions in your device system settings.',
	},
	[KEYBOARD_SETTING_IDS.LAYOUT_SYNC]: {
		type: SettingType.RADIO,
		title: 'Keyboard layout synchronization',
		description:
			'Select how the keyboard layout of your device synchronizes with the keyboard layout of the VDA.',
		options: [
			{
				value: 'syncOnceSessionStart',
				label: 'Sync once at session start',
				description:
					'The local client keyboard layout is applied to the virtual session at the start of the session.',
			},
			{
				value: 'dynamicallySync',
				label: 'Dynamically sync',
				description:
					'The local client keyboard layout is applied when you switch between keyboard layouts.',
			},
			{
				value: 'dontSync',
				label: "Don't sync",
				description: 'Only the virtual session keyboard layout is used.',
			},
		],
	},
	[KEYBOARD_SETTING_IDS.IME]: {
		type: SettingType.RADIO,
		title: 'Input Method Editor (IME)',
		description:
			'Citrix Workspace app improved the client IME input experience, but some features may not be fully compatible. If you encounter any issues, use the legacy client IME user experience.',
		options: [
			{
				value: 'modernClientIME',
				label: 'Modern client IME user experience',
				description: '',
			},
			{
				value: 'legacyClientIME',
				label: 'Legacy client IME user experience',
				description: '',
			},
		],
	},
	[KEYBOARD_SETTING_IDS.SHOW_LANGUAGE_BAR]: {
		type: SettingType.TOGGLE,
		title: 'Show language bar in virtual session',
		description:
			'The language bar is displayed in the virtual session so you can easily switch between languages.',
	},
	[KEYBOARD_SETTING_IDS.KEYBOARD_INPUT_MODE]: {
		type: SettingType.NAVIGATION,
		title: 'Keyboard input mode',
		description: 'Select your preferred keyboard input mode.',
		onNavigating: () => {
			console.log('Navigate to Keyboard Input Mode Settings');
		},
	},
	[KEYBOARD_SETTING_IDS.KEYBOARD_INPUT_MODE_SELECTION]: {
		type: SettingType.RADIO,
		title: '',
		description: '',
		options: [
			{
				value: 'scancode',
				label: 'Scancode',
				description:
					'Uses the keyboard layout of your device, regardless of the virtual session keyboard layout.',
			},
			{
				value: 'unicode',
				label: 'Unicode',
				description:
					'Uses the keyboard layout of your device, regardless of the virtual session keyboard layout.',
			},
			{
				value: 'automatic',
				label: 'Automatic',
				description:
					"Automatically selects the appropriate keyboard input mode based on the OS of the virtual desktop you're connecting to. Typically, this means Unicode for Windows and Linux, and Scancode for macOS.",
			},
		],
	},

	[KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.UNICODE_MODE]: {
		type: SettingType.Plain,
		title: 'Unicode mode',
		description: '',
		showContentSeparator: true,
		subSettings: [
			{
				settingId: KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.CTRL_CHARACTER,
				type: SettingType.RADIO,
				title: 'Send Ctrl character using',
				description: '',
				options: [
					{ value: 'control', label: '^Control', description: '' },
					{
						value: 'command-or-control',
						label: '⌘ Command (left) or ^ Control',
						description: '',
					},
				],
			} as RadioSettingConfig,
			{
				settingId: KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.ALT_CHARACTER,
				type: SettingType.RADIO,
				title: 'Send Alt character using',
				description: '',
				options: [
					{ value: 'command', label: '⌘ Command (left)', description: '' },
					{ value: 'command-option', label: '⌘⌥ Command (left)-Option', description: '' },
				],
			} as RadioSettingConfig,
			{
				settingId: KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.WINDOWS_LOGO_KEY,
				type: SettingType.TOGGLE,
				title: 'Send Windows logo key using ⌘ Command (right)',
				description: '',
			} as ToggleSettingConfig,
			{
				settingId: KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.FKEYS_WITH_OPTION,
				type: SettingType.TOGGLE,
				title: 'Send F1-F12 keys with Option (left) and the corresponding keypad keys',
				description:
					'For example, press Option (left)+1 (on the numeric keypad) to send the F1 key.',
			} as ToggleSettingConfig,
		],
	},

	// Workspace Update Settings
	[WORKSPACE_UPDATE_SETTING_IDS.NOTIFY_UPDATES]: {
		type: SettingType.TOGGLE,
		title: 'Notify me when new updates are available',
		description: '',
		subSettings: [
			{
				settingId: WORKSPACE_UPDATE_SETTING_IDS.TRIGGER_UPDATE_DOWNLOAD,
				type: SettingType.BUTTON,
				title: '',
				description: '',
				buttons: [
					{
						label: 'Check for updates',
						variant: 'primary',
					},
				],
			} as ButtonSettingConfig,
		],
	},
	[WORKSPACE_UPDATE_SETTING_IDS.UPDATE_CHANNEL]: {
		type: SettingType.RADIO,
		title: 'Update channel',
		description:
			'Choose how you receive application updates, whether you prefer the latest stable version or early access to new features.',
		learnMoreText: 'Learn more',
		learnMoreUrl: 'https://docs.citrix.com/en-us/citrix-workspace-app/workspace-update',
		options: [
			{
				value: 'release',
				label: 'Generally Available',
				description:
					'Receive fully supported application versions ensuring stability and reliability.',
			},
			{
				value: 'beta',
				label: 'Early Access Release',
				description:
					'Access new features early for testing and feedback before general release. This should not be used for everyday work purposes.',
			},
			{
				value: 'canary',
				label: 'Canary',
				description:
					'Receive daily builds from the master branch for internal developer testing. Builds may include unstable or experimental features.',
			},
			{
				value: 'dev',
				label: 'Dev',
				description:
					'Receive daily builds from release branches for internal tester use. Builds may include unstable or pre-release features.',
			},
		],
	},

	// General Settings

	[GENERAL_SETTING_IDS.WORKSPACE_UPDATE]: {
		type: SettingType.NAVIGATION,
		title: 'Citrix Workspace update',
		description: 'Stay up-to-date with the latest version of the app.',
		onNavigating: () => {
			console.log('Navigate to Workspace Update Settings');
		},
	},
	[GENERAL_SETTING_IDS.CONNECTION_NOTIFICATIONS]: {
		type: SettingType.TOGGLE,
		title: 'Connection strength notifications',
		description:
			'Alert in-session notifications when your connection drops to poor or fails.',
	},
	[GENERAL_SETTING_IDS.SHORTCUTS]: {
		type: SettingType.NAVIGATION,
		title: 'Shortcuts',
		description:
			'Set touch device shortcuts for quick access to virtual apps and desktops.',
		onNavigating: () => {
			console.log('Navigate to Shortcuts Settings');
		},
	},
	[GENERAL_SETTING_IDS.FILE_DOWNLOAD_LOCATION]: {
		type: SettingType.FILE_DOWNLOAD,
		title: 'File download location',
		description:
			'Select where to download files from a virtual session with your local device.',
		options: [
			{
				value: 'same-location',
				label: 'Download files to the same location every time',
				description: '',
			},
			{
				value: 'ask-every-time',
				label: 'Ask me where to download files every time',
				description: '',
			},
		],
		allowCustomPath: true,
		filePickerConfig: {
			buttonLabel: 'Change',
			placeholder: 'Enter file download location...',
		},
	},
	[GENERAL_SETTING_IDS.SESSION_RECONNECT]: {
		type: SettingType.TOGGLE,
		title: 'Session reconnect',
		description:
			'Automatically reconnect to app or desktop sessions from any device when conditions are met.',
		subSettings: [
			{
				label: 'Reconnect to',
				options: [
					{
						value: 'active-and-disconnected',
						label: 'Active and disconnected sessions',
						description: '',
					},
					{
						value: 'active-only',
						label: 'Active sessions only',
						description: '',
					},
					{
						value: 'disconnected-only',
						label: 'Disconnected sessions only',
						description: '',
					},
				],
			} as DropdownSettingConfig,
			{
				toggleOptions: [
					{
						id: GENERAL_SETTING_IDS.RECONNECT_ON_APP_DESKTOP,
						title: 'When I open an app or desktop',
						description: '',
					},
					{
						id: GENERAL_SETTING_IDS.RECONNECT_ON_WORKSPACE_REFRESH,
						title: 'When I refresh Citrix Workspace',
						description: '',
					},
					{
						id: GENERAL_SETTING_IDS.RECONNECT_ON_WORKSPACE_LOGIN,
						title: 'When I log in to Citrix Workspace',
						description: '',
					},
					{
						id: GENERAL_SETTING_IDS.RECONNECT_ON_WINDOWS_LOGIN,
						title: 'When I log in to Windows',
						description: '',
					},
				],
			} as ToggleSettingGroupConfig,
		],
	},
	// Mac-specific General Settings
	[GENERAL_SETTING_IDS.START_ON_SYSTEM_STARTUP]: {
		type: SettingType.TOGGLE,
		title: 'Start Workspace on system startup',
		description:
			'Controls whether the Citrix Workspace app launches automatically every time you start or restart your Mac.',
	},
	[GENERAL_SETTING_IDS.SHOW_IN_MENU_BAR]: {
		type: SettingType.TOGGLE_SETTING_GROUP,
		title: 'Menu bar',
		description: '',
		toggleOptions: [
			{
				id: GENERAL_SETTING_IDS.MENU_BAR_SHOW_WORKSPACE,
				title: 'Show Workspace in the menu bar',
				description: '',
			},
			{
				id: GENERAL_SETTING_IDS.MENU_BAR_SHOW_QUICK_ACCESS,
				title: 'Show Quick Access for StoreFront experience',
				description: '',
			},
		],
	},
	[GENERAL_SETTING_IDS.MAC_SESSION_RECONNECT]: {
		type: SettingType.TOGGLE_SETTING_GROUP,
		title: 'Session reconnect',
		description:
			'Select when to automatically reconnect to an app or desktop session from any device.',
		toggleOptions: [
			{
				id: GENERAL_SETTING_IDS.MAC_RECONNECT_ON_WORKSPACE_LOGIN,
				title: 'When I log in to Citrix Workspace',
				description: '',
			},
			{
				id: GENERAL_SETTING_IDS.MAC_RECONNECT_ON_APP_DESKTOP_REOPEN,
				title: 'When I reopen or refresh an app or desktop',
				description: '',
			},
		],
	},
	[GENERAL_SETTING_IDS.ABOUT]: {
		type: SettingType.NAVIGATION,
		title: 'About',
		description: 'Citrix Workspace app specifications, third party notices, and more.',
		onNavigating: () => {
			console.log('Navigate to About Settings');
		},
	},

	// Shortcuts Settings
	[SHORTCUT_SETTING_IDS.START_MENU_DIRECTORY_SETTING]: {
		type: SettingType.TOGGLE,
		title: 'Show applications in the Start Menu',
		description: '',
		subSettings: [
			{
				type: SettingType.TEXT_INPUT,
				label: 'Start Menu folder',
				placeholder: 'Enter folder name',
				helpText:
					'All apps, both new and existing, are moved to the specified folder in the Start Menu. You can choose to use a new or existing folder.',
			} as TextInputSettingConfig,
		],
	},
	[SHORTCUT_SETTING_IDS.DESKTOP_DIRECTORY_SETTING]: {
		type: SettingType.TOGGLE,
		title: 'Show applications on my local desktop',
		description: '',
		subSettings: [
			{
				type: SettingType.TEXT_INPUT,
				label: 'Desktop folder',
				placeholder: 'Enter folder name',
				helpText:
					'All apps, both new and existing, are moved to the specified folder on your local desktop.',
			} as TextInputSettingConfig,
		],
	},
	[SHORTCUT_SETTING_IDS.ORGANIZE_BY_CATEGORIES]: {
		type: SettingType.TOGGLE_SETTING_GROUP,
		title: 'Organize by app categories',
		description: 'Create separate folders for different application categories',
		showContentSeparator: true,
		toggleOptions: [
			{
				id: SHORTCUT_SETTING_IDS.ORGANIZE_START_MENU_CATEGORIES,
				title: 'Create separate folders in the Start Menu for different app categories',
				description: '',
			},
			{
				id: SHORTCUT_SETTING_IDS.ORGANIZE_DESKTOP_CATEGORIES,
				title: 'Create separate folders on my local desktop for different app categories',
				description: '',
			},
		],
	},

	// Privacy & Security Settings
	[PRIVACY_SECURITY_SETTING_IDS.SEND_USAGE_STATISTICS]: {
		type: SettingType.TOGGLE,
		title: 'Send usage statistics and data to Citrix',
		description:
			'Help improve the Citrix Workspace app by sending anonymous configuration and usage data to Citrix. This data is only analyzed to enhance app quality and performance, and no personally identifiable information is ever collected.',
		ariaLabel: 'Send usage statistics',
		learnMoreUrl:
			'https://docs.citrix.com/en-us/citrix-workspace-app/privacy-data-collection',
		learnMoreText: 'Learn more',
	},
	[PRIVACY_SECURITY_SETTING_IDS.TRIGGER_DELETE_PASSWORDS]: {
		type: SettingType.BUTTON,
		title: 'Delete passwords',
		description:
			'Delete your saved store user name and passwords so that Citrix Workspace asks you for your credentials the next time that you log in. This action does not remove your store, only your saved credentials.',
		buttons: [
			{
				label: 'Delete passwords',
				variant: 'secondary',
			},
		],
	},
	[PRIVACY_SECURITY_SETTING_IDS.SSO_ENTRA_ID]: {
		type: SettingType.TOGGLE,
		title: 'Seamless single sign-on (SSO) for Microsoft Entra ID',
		description:
			"Allows Citrix Workspace app to use your device's Microsoft Entra credentials for seamless, password-free sign-on.",
	},
	[PRIVACY_SECURITY_SETTING_IDS.LINUX_PRIVACY_FEATURE]: {
		type: SettingType.TOGGLE,
		title: 'Enhanced Linux Privacy Mode (Linux Only)',
		description:
			'Enable enhanced privacy protections specifically designed for Linux environments. This setting is only available on Linux platforms.',
	},
	[PRIVACY_SECURITY_SETTING_IDS.TLS_Settings]: {
		type: SettingType.NAVIGATION,
		title: 'TLS',
		description: 'Control how TLS is configured for secured HDX connections.',
		onNavigating: () => {
			console.log('Navigate to TLS Settings');
		},
	},
	[TLS_SETTING_IDS.TLS_REQUIRE_FOR_ALL]: {
		type: SettingType.TOGGLE,
		title: 'Require TLS for all connections',
		description:
			'Enforce TLS encryption for all connections to ensure secure communication.',
	},
	[TLS_SETTING_IDS.TLS_COMPLIANCE_MODE]: {
		type: SettingType.RADIO,
		title: 'Compliance mode',
		description: 'Select the security compliance mode for the policy.',
		options: [
			{ value: 'none', label: 'None' },
			{ value: 'sp800-52', label: 'SP800-52' },
		],
	},
	[TLS_SETTING_IDS.TLS_CRYPTO_MODULE]: {
		type: SettingType.RADIO,
		title: 'Crypto module',
		description:
			'Select the crypto module to use for cryptographic work (encryption, decryption, etc.).',
		options: [
			{
				value: 'standard',
				label: 'Standard',
				description:
					'Use the native, built-in cryptographic library that is part of macOS.',
			},
			{
				value: 'fips',
				label: 'FIPS',
				description:
					'Use a special, embedded cryptographic module that Citrix provides within the app.',
			},
		],
	},
	[TLS_SETTING_IDS.TLS_VERSION]: {
		type: SettingType.RADIO,
		title: 'TLS version',
		description:
			'Select the TLS protocol versions that are accepted during protocol negotiation.',
		options: [
			{ value: 'tlsv1.2', label: 'TLSv1.2' },
			{ value: 'tlsv1.2-and-tlsv1.3', label: 'TLSv1.2 and TLSv1.3' },
		],
	},
	[TLS_SETTING_IDS.TLS_CERTIFICATE_REVOCATION]: {
		type: SettingType.RADIO,
		title: 'Certificate revocation check',
		description:
			"Select how Citrix Workspace checks a server's security certificate on the Certificate Revocation List (CRL).",
		options: [
			{
				value: 'off',
				label: 'Off',
				description: 'Disables all CRL security checks.',
			},
			{
				value: 'attempt-local-cache',
				label: 'Attempt local cache check',
				description: 'Only looks at CRL files already stored on the computer.',
			},
			{
				value: 'attempt-local-cache-and-network',
				label: 'Attempt local cache check and network check',
				description:
					"Looks at CRL files already stored on the computer and checks the internet for the latest CRLs, but will not fail if it can't find them.",
			},
			{
				value: 'require-except-root',
				label: 'Require for all certificates except root',
				description:
					"Looks at CRL files already stored on the computer and checks the internet for the latest CRLs, and makes finding for CRLs mandatory. The connection will be blocked if it can't be verified.",
			},
			{
				value: 'require-all',
				label: 'Require for all certificates',
				description:
					"Looks at CRL files already stored on the computer and checks the internet for the latest CRLs, and makes finding CRLs mandatory. Adds a mandatory check for the root certificate. The connection will be blocked if it can't be verified.",
			},
		],
	},
	[PRIVACY_SECURITY_SETTING_IDS.SMARTCARD]: {
		type: SettingType.DROPDOWN,
		title: 'Smartcard',
		description:
			'Configuring a PKCS#11 module is not usually necessary. Only do so if instructed by your administrator.',
		label: 'PKCS#11',
		options: [
			{ value: 'none', label: 'None selected', description: 'No smartcard selected' },
			{ value: 'others', label: 'Others', description: 'Other smartcard configurations' },
		],
	},
	[PRIVACY_SECURITY_SETTING_IDS.PERMISSIONS]: {
		type: SettingType.PERMISSIONS_SETTING,
		title: 'Permissions',
		description: '',
		showContentSeparator: true,
		subsettings: {
			[PERMISSIONS_SETTING_IDS.FILE_ACCESS]: {
				title: 'File access',
				type: SettingType.RADIO,
				description: '',
				label: 'Select a store',
				options: [
					{
						value: 'readAndWrite',
						label: 'Read and write',
					},
					{
						value: 'readOnly',
						label: 'Read only',
					},
					{
						value: 'noAccess',
						label: 'No access',
					},
					{
						value: 'askEveryTime',
						label: 'Ask me every time',
					},
				],
			},
			[PERMISSIONS_SETTING_IDS.BUILTIN_MICROPHONE_AND_CAMERA]: {
				title: 'Built-in Microphone and Camera',
				type: SettingType.RADIO,
				description: '',
				label: 'Select a store',
				options: [
					{
						value: 'connectAutomatically',
						label: 'Connect automatically',
					},
					{
						value: 'dontConnect',
						label: "Don't connect",
					},
					{
						value: 'askEveryTime',
						label: 'Ask me every time',
					},
				],
			},
		},
	},
	[PRIVACY_SECURITY_SETTING_IDS.WINDOWS_PERMISSIONS]: {
		type: SettingType.RADIO_SETTING_GROUP,
		title: 'Permissions',
		description: '',
		showContentSeparator: true,
		radioOptions: [
			{
				id: WINDOWS_PERMISSIONS_SETTING_IDS.FILE_ACCESS,
				title: 'File access',
				description: '',
				options: [
					{
						value: 'readAndWrite',
						label: 'Read and write',
					},
					{
						value: 'readOnly',
						label: 'Read only',
					},
					{
						value: 'noAccess',
						label: 'No access',
					},
					{
						value: 'askMeEverytime',
						label: 'Ask me every time',
					},
				],
			},
			{
				id: WINDOWS_PERMISSIONS_SETTING_IDS.MICROPHONES_AND_WEBCAMS,
				title: 'Microphones and webcams',
				description: '',
				options: [
					{
						value: 'connectAutomatically',
						label: 'Connect automatically',
					},
					{
						value: 'dontConnect',
						label: "Don't connect",
					},
					{
						value: 'askMeEveryTime',
						label: 'Ask me every time',
					},
				],
			},
			{
				id: WINDOWS_PERMISSIONS_SETTING_IDS.DIGITAL_CAMERAS_AND_SCANNERS,
				title: 'Digital cameras and scanners',
				description: '',
				options: [
					{
						value: 'connectAutomatically',
						label: 'Connect automatically',
					},
					{
						value: 'dontConnect',
						label: "Don't connect",
					},
					{
						value: 'askMeEveryTime',
						label: 'Ask me every time',
					},
				],
			},
		],
	},

	[CAMERA_SETTING_IDS.CAMERA_ACCESS]: {
		type: SettingType.TOGGLE_SETTING_GROUP,
		title: 'Camera',
		description: '',
		toggleOptions: [
			{
				id: CAMERA_SETTING_IDS.CAMERA_ACCESS,
				title: 'Background blur',
			},
		],
	},
	[CAMERA_SETTING_IDS.AVAILABLE_CAMERA]: {
		type: SettingType.DROPDOWN,
		title: 'Available camera',
		description:
			'The session will connect only to the selected camera. When System default is selected, all available cameras are connected.',
		label: '',
		options: [],
	},
	[CAMERA_SETTING_IDS.BACKGROUND_BLUR]: {
		type: SettingType.TOGGLE,
		title: 'Background blur',
		description: '',
	},

	// Audio Settings
	[AUDIO_SETTING_IDS.DEFAULT_SPEAKER]: {
		type: SettingType.DROPDOWN,
		title: 'Default speaker',
		description:
			'The session will connect to the selected speaker by default. When the selected speaker is not available, the system default is connected.',
		label: '',
		options: [],
	},
	[AUDIO_SETTING_IDS.DEFAULT_MICROPHONE]: {
		type: SettingType.DROPDOWN,
		title: 'Default microphone',
		description:
			'The session will connect to the selected microphone by default. When the selected microphone is not available, the system default is connected.',
		label: '',
		options: [],
	},
	[AUDIO_SETTING_IDS.NOISE_SUPPRESSION]: {
		type: SettingType.RADIO,
		title: 'Noise suppression',
		description:
			'Select the noise suppression level for improved audio quality, especially during video calls and meetings.',
		options: [
			{
				value: 'none',
				label: 'None',
				description:
					'This is the default setting and does not affect resource consumption.',
			},
			{
				value: 'low',
				label: 'Low',
				description: 'Low\nNoise reduction is minimal with less reource consumption.',
			},
			{
				value: 'high',
				label: 'High',
				description:
					'High\nNoise reduction is at maximum intensity with higher resource consumption.',
			},
		],
	},

	// Device Settings
	[DEVICE_SETTING_IDS.AUTO_CONNECT_ON_SESSION_START]: {
		type: SettingType.TOGGLE,
		title: 'Connect devices automatically when a session starts',
		description: '',
	},
	[DEVICE_SETTING_IDS.AUTO_CONNECT_NEW_DEVICE]: {
		type: SettingType.TOGGLE,
		title:
			"Connect a new device automatically when it's connected during an active session",
		description: '',
	},
	[DEVICE_SETTING_IDS.DEVICES_INFO]: {
		type: SettingType.Plain,
		title: 'USB Devices',
		description:
			'Use your local USB devices, such as scanners or signature pads, within your remote session. Your administrator determines which devices are supported.',
		learnMoreText: 'Learn more',
		learnMoreUrl: 'https://docs.citrix.com/en-us/citrix-workspace-app/usb-devices',
	},

	// Mouse Settings
	[MOUSE_SETTING_IDS.USE_RELATIVE_MOUSE]: {
		type: SettingType.TOGGLE,
		title: 'Use relative mouse',
		description:
			'Determines how far the mouse has moved since the last frame within a window or screen. This is useful when manipulating 3D objects.',
	},
	[MOUSE_SETTING_IDS.CONSTRAIN_POINTER_TO_WINDOW]: {
		type: SettingType.TOGGLE,
		title: 'Constraint pointer to window',
		description:
			"Restricts your mouse pointer's movement to stay within the boundaries of the session window.",
	},
	[MOUSE_SETTING_IDS.APPLY_WINDOW_SCALING]: {
		type: SettingType.TOGGLE,
		title: 'Apply window scaling to mouse movement',
		description:
			"Attempts to compensate for your local display's DPI scaling when it transmits mouse movement and click data to the virtual session.",
	},

	// Collect Logs Settings
	[COLLECT_LOGS_SETTING_IDS.LOG_COLLECTION]: {
		type: SettingType.RADIO,
		title: 'Select Log level',
		description: 'Select the log level you want to collect.',
		showContentSeparator: true,
		options: [
			{
				value: 'low',
				label: 'Low',
				description: 'Minimum logs are collected for basic troubleshooting.',
			},
			{
				value: 'medium',
				label: 'Medium',
				description:
					'Identifies errors while connecting. All logging is enabled up until the point when the session is successful.',
			},
			{
				value: 'verbose',
				label: 'Verbose',
				description:
					'Captures everything including the connection diagnostics. Citrix Workspace app will store up to 10 session logs.',
			},
		],
	},
};

/**
 * Helper function to get setting config by ID
 * @param settingId The setting ID to look up
 * @returns The setting configuration or undefined if not found
 */
export function getSettingConfig(settingId: string): SettingConfig | undefined {
	return SETTINGS_DEFINITIONS[settingId];
}

/**
 * Helper function to check if a setting exists
 * @param settingId The setting ID to check
 * @returns True if the setting exists
 */
export function hasSettingConfig(settingId: string): boolean {
	return settingId in SETTINGS_DEFINITIONS;
}

/**
 * Helper function to get all setting IDs
 * @returns Array of all setting IDs
 */
export function getAllSettingIds(): string[] {
	return Object.keys(SETTINGS_DEFINITIONS);
}

/**
 * Utility function to get setting title by ID
 * @param settingId The setting ID to look up
 * @returns The setting title or empty string if not found
 */
export function getSettingTitle(settingId: string): string {
	const config = SETTINGS_DEFINITIONS[settingId];
	return config?.title || '';
}

/**
 * Utility function to get setting description by ID
 * @param settingId The setting ID to look up
 * @returns The setting description or empty string if not found
 */
export function getSettingDescription(settingId: string): string {
	const config = SETTINGS_DEFINITIONS[settingId];
	return config?.description || '';
}

/**
 * Utility function to get setting options for radio/iconradio types
 * @param settingId The setting ID to look up
 * @returns The setting options or empty array if not found or not applicable
 */
export function getSettingOptions(
	settingId: string
): Array<{ value: string; label: string; description?: string; icon?: string }> {
	const config = SETTINGS_DEFINITIONS[settingId];
	if (config && 'options' in config) {
		return config.options || [];
	}
	return [];
}
